import { useImperativeHandle, useMemo } from 'react';

import { FlexAligner } from '@scaleaq/scaleui';
import { InventoryHelper } from '@scaleaq/sitecontrol-lib';
import { DateTime, Duration } from 'luxon';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ControlledDateField } from '@/components/inputFields/ControlledDateField';
import { ControlledRadioField } from '@/components/inputFields/ControlledRadioField';
import { ControlledSelectField } from '@/components/inputFields/ControlledSelectField';
import { ControlledTextOrNumberField } from '@/components/inputFields/ControlledTextOrNumberField';
import { useGetInventoriesSensors } from '@/dataAccess/rest';
import { useClassInventoryProviderPair } from '@/hooks';
import { responsive } from '@/utils';

import { useSiteContext } from '../../../../providers';
import { useChartPlotTypes, useLiveChartOptions } from '../../hooks.ts';
import {
  ChartConfigProps,
  ChartInterval,
  ChartType,
  PlotType,
  ScatterPlotChartConfigType,
} from '../../types.ts';

export const ScatterPlotChartConfig = ({
  config,
  onSave,
  id,
  ref,
  rootId,
}: ChartConfigProps<ScatterPlotChartConfigType>) => {
  const { t } = useTranslation(['chartConfig', 'common']);
  const { classInventoryProviderPair } = useClassInventoryProviderPair();
  const { currentSite: { id: inventoryId } = {} } = useSiteContext();
  const getDefaultValues = () => {
    const now = DateTime.now();

    return {
      type: ChartType.LIVE,
      interval: ChartInterval.Day,
      start: now.minus(Duration.fromObject({ days: 1 })),
      end: now,
    };
  };

  const {
    control,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<ScatterPlotChartConfigType>({
    defaultValues: { ...getDefaultValues(), ...config, plotType: PlotType.SCATTER },
  });

  const providerAWatcher = useWatch({ control, name: `providerA` });
  const providerBWatcher = useWatch({ control, name: `providerB` });
  const sensorAWatcher = useWatch({ control, name: 'sensorIdA' });
  const sensorBWatcher = useWatch({ control, name: 'sensorIdB' });
  const chartTypeWatcher = useWatch({ control, name: 'type' });

  const { data: sensors, isLoading: isLoadingSensors } = useGetInventoriesSensors(inventoryId);

  const { chartTypeOptions, chartIntervalOptions } = useLiveChartOptions();

  const relatedSensorsA = useMemo(
    () =>
      (
        sensors
          ?.filter(s => (!rootId ? true : s.path.indexOf(rootId) !== -1))
          .filter(
            s =>
              (!providerAWatcher ||
                s.privateProviders.includes(classInventoryProviderPair[providerAWatcher])) &&
              s.privateProviders.some(p => InventoryHelper.numericalProviders.includes(p))
          ) ?? []
      ).map(sensor => ({
        label: sensor.name,
        value: sensor.id, // Assuming you want the first numerical provider
      })),
    [classInventoryProviderPair, providerAWatcher, rootId, sensors]
  );
  const relatedSensorsB = useMemo(
    () =>
      (
        sensors
          ?.filter(s => (!rootId ? true : s.path.indexOf(rootId) !== -1))
          .filter(
            s =>
              (!providerBWatcher ||
                s.privateProviders.includes(classInventoryProviderPair[providerBWatcher])) &&
              s.privateProviders.some(p => InventoryHelper.numericalProviders.includes(p))
          ) ?? []
      ).map(sensor => ({
        label: sensor.name,
        value: sensor.id, // Assuming you want the first numerical provider
      })),
    [classInventoryProviderPair, providerBWatcher, rootId, sensors]
  );

  const { plotTypes: plotTypesA, isLoading: isLoadingPlotTypesA } = useChartPlotTypes(
    sensorAWatcher ? [sensorAWatcher] : []
  );
  const { plotTypes: plotTypesB, isLoading: isLoadingPlotTypesB } = useChartPlotTypes(
    sensorBWatcher ? [sensorBWatcher] : []
  );

  const triggerSave = async () => {
    const isValid = await trigger();
    if (isValid) onSave(id, getValues());
  };

  useImperativeHandle(ref, () => ({
    save: () => triggerSave().catch(err => console.error(err)),
  }));

  return (
    <FlexAligner direction={'column'} gap={responsive.spacing['5xlarge']} fullWidth>
      <ControlledTextOrNumberField control={control} name={'title'} label={t('common:title')} />
      <FlexAligner gap={responsive.spacing.large}>
        <ControlledSelectField
          label={t('chartConfig:scatterPlot.label.sensorA')}
          placeholder={t('chartConfig:placeholder.sensor')}
          control={control}
          name={'sensorIdA'}
          rules={{
            required: t('chartConfig:scatterPlot.isRequired.sensorA'),
          }}
          caution={errors.sensorIdA?.message}
          options={relatedSensorsA}
          isLoading={isLoadingSensors}
          isClearable={true}
        />
        <ControlledSelectField
          label={t('chartConfig:scatterPlot.label.typeA')}
          placeholder={t('chartConfig:placeholder.type')}
          name={'providerA'}
          control={control}
          options={plotTypesA}
          rules={{
            required: t('chartConfig:scatterPlot.isRequired.typeA'),
          }}
          caution={errors.providerA?.message}
          isLoading={isLoadingPlotTypesA}
          isClearable={true}
        />
      </FlexAligner>
      <FlexAligner gap={responsive.spacing.large}>
        <ControlledSelectField
          label={t('chartConfig:scatterPlot.label.sensorB')}
          placeholder={t('chartConfig:placeholder.sensor')}
          control={control}
          name={'sensorIdB'}
          rules={{
            required: t('chartConfig:scatterPlot.isRequired.sensorB'),
          }}
          caution={errors.sensorIdB?.message}
          options={relatedSensorsB}
          isLoading={isLoadingSensors}
          isClearable={true}
        />
        <ControlledSelectField
          label={t('chartConfig:scatterPlot.label.typeB')}
          placeholder={t('chartConfig:placeholder.type')}
          name={'providerB'}
          control={control}
          options={plotTypesB}
          rules={{
            required: t('chartConfig:scatterPlot.isRequired.typeB'),
          }}
          caution={errors.providerB?.message}
          isLoading={isLoadingPlotTypesB}
          isClearable={true}
        />
      </FlexAligner>
      <ControlledRadioField
        control={control}
        name={'type'}
        label={t('chartConfig:chartConfigModal.fields.chartType')}
        options={chartTypeOptions}
        rules={{
          required: t('chartConfig:chartConfigModal.rules.isRequired.chartType'),
        }}
        caution={errors.type?.message}
      />
      <ControlledRadioField
        render={chartTypeWatcher === ChartType.LIVE}
        control={control}
        name={'interval'}
        label={t('chartConfig:chartConfigModal.fields.interval')}
        options={chartIntervalOptions}
        rules={{
          required: t('chartConfig:chartConfigModal.rules.isRequired.interval'),
        }}
        caution={errors.interval?.message}
      />
      <ControlledDateField
        render={chartTypeWatcher === ChartType.HISTORICAL}
        control={control}
        name={'start'}
        label={t('chartConfig:chartConfigModal.fields.from')}
        rules={{
          required: t('chartConfig:scatterPlot.isRequired.start'),
        }}
        caution={errors.start?.message}
      />
      <ControlledDateField
        render={chartTypeWatcher === ChartType.HISTORICAL}
        control={control}
        name={'end'}
        label={t('chartConfig:chartConfigModal.fields.to')}
        rules={{
          required: t('chartConfig:scatterPlot.isRequired.end'),
        }}
        caution={errors.end?.message}
      />
    </FlexAligner>
  );
};
